import { computed, reactive, watch, type Ref } from "vue";
import type {
  DeliveryAreaSectionListInput,
  DeliveryAreaSectionCountInput,
  ListDeliveryAreaSectionAssignedQuery,
  DeleteDeliveryAreaSectionMutation,
  DeleteDeliveryAreaSectionMutationVariables,
} from "@/generated/graphql";
import {
  ListDeliveryAreaSectionAssignedDocument,
  DeleteDeliveryAreaSectionDocument,
} from "@/generated/graphql";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { localStorageGetCustomerCode, localStorageGetUserId } from "@/common/localStorageUtil";
import { displayPagingRange as calcDisplayPagingRange } from "@/common/common";
import type { DeliveryAreaInput, AreaSelectValue, ShopSelectValue, ChainSelectValue, AssignedSection, AssignedItem } from "../types";

/**
 * composableへ渡す依存をまとめた型。
 */
interface UseAreaListSectionAssignedParams {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  overlay: Ref<boolean>;
  displayConfirmDialogSectionAssigned: Ref<boolean>;
  areaSelectValuesSectionAssigned: Ref<AreaSelectValue>;
  shopSelectValues: Ref<ShopSelectValue>;
  chainSelectValues: Ref<ChainSelectValue>;
  deliveryAreaInput: DeliveryAreaInput;
  listLoading: Ref<boolean>;
  listAreaSectionAssigned: Ref<AssignedSection>;
  selectListCheckboxSectionAssigned: Ref<string[]>;
  searchConditionZipCodeSectionAssigned: Ref<string | null>;
  listDataTotalCountSectionAssigned: Ref<number>;
  displayPagingRangeAssigned: Ref<string>;
}

/**
 * 画面ロジックを扱う composable。
 */
export const useAreaListSectionAssigned = ({
  errdialog,
  errMessage,
  overlay,
  displayConfirmDialogSectionAssigned,
  areaSelectValuesSectionAssigned,
  shopSelectValues,
  chainSelectValues,
  deliveryAreaInput,
  listLoading,
  listAreaSectionAssigned,
  selectListCheckboxSectionAssigned,
  searchConditionZipCodeSectionAssigned,
  listDataTotalCountSectionAssigned,
  displayPagingRangeAssigned,
}: UseAreaListSectionAssignedParams) => {
  // ページ跨ぎの選択アイテムデータをキャッシュするMap（lot_code → アイテムデータ）
  const selectedItemsMap = reactive(new Map<string, AssignedItem>());

  // 選択されたlot_codeに対応するフルデータを取得するcomputed（キャッシュから取得）
  const selectedAssignedItems = computed(() =>
    Array.from(selectedItemsMap.values()).filter(
      (item): item is NonNullable<typeof item> => item != null
    )
  );

  // テーブルの選択変更ハンドラ（ページ跨ぎ対応）
  const onSelectionChangeAssigned = (newSelection: string[]) => {
    // 現在のページに表示されているアイテムのlot_codeセット
    const currentPageLotCodes = new Set(
      listAreaSectionAssigned.value
        .filter((item): item is NonNullable<typeof item> => item?.lot_code != null)
        .map(item => item.lot_code!)
    );
    const newSelectionSet = new Set(newSelection);

    // 現在ページのアイテムについて、選択/解除をキャッシュに反映
    for (const lotCode of currentPageLotCodes) {
      if (newSelectionSet.has(lotCode)) {
        // 選択された → キャッシュに追加
        if (!selectedItemsMap.has(lotCode)) {
          const item = listAreaSectionAssigned.value.find(i => i?.lot_code === lotCode);
          if (item) {
            selectedItemsMap.set(lotCode, item);
          }
        }
      } else {
        // 選択解除された → キャッシュから削除
        selectedItemsMap.delete(lotCode);
      }
    }

    // selectListCheckboxSectionAssignedをキャッシュの全キーで更新
    selectListCheckboxSectionAssigned.value = Array.from(selectedItemsMap.keys());
  };

  const pageCountSectionAssigned= computed(() =>
    Math.max(1, Math.ceil(listDataTotalCountSectionAssigned.value / (assignedListOptions.itemsPerPage || 1)))
  );

  const assignedListOptions = reactive({
    page: 1,
    itemsPerPage: 20,
    sortBy: [] as string[],
    sortDesc: [] as boolean[],
    groupBy: [] as string[],
    groupDesc: [] as boolean[],
    multiSort: false,
    mustSort: false,
  });

  // 登録済配達区画オプション変更時
  watch(
    () => assignedListOptions,
    async () => {
      listLoading.value = true;
      await onClickSearchAssigned(true);
      listLoading.value = false;
    },
    { deep: true }
  );

  // 登録済配達区画一覧 オプション更新
  const onUpdateAssignedOptions = (newOptions: { page: number; itemsPerPage: number; sortBy: Array<{ key: string; order: "asc" | "desc" }> }): void => {
    assignedListOptions.page = newOptions.page;
    assignedListOptions.itemsPerPage = newOptions.itemsPerPage;
    assignedListOptions.sortBy = newOptions.sortBy.map(s => s.key);
  };

  // 一括削除 GraphQL 実行
  const bulkDeleteExecute = async (
    customer_code: string,
    chain_code: string,
    shop_code: string,
    area_code: string,
    lot_list: string[],
    user_id: string
  ): Promise<boolean> => {
    try {
      const apolloClient = await generateApolloClient();

      // 配達区画登録
      const result = await apolloClient.mutate<DeleteDeliveryAreaSectionMutation, DeleteDeliveryAreaSectionMutationVariables>({
        mutation: DeleteDeliveryAreaSectionDocument,
        variables: { input: { customer_code, chain_code, shop_code, area_code, lot_list, user_id } },
      });
      if (!result.data) {
        console.error("一括削除エラー");
        return false;
      }
      return true;
    } catch (err: unknown) {
      console.error(err instanceof Error ? err.message : err);
      return false;
    }
  };

  // 登録済区画一覧検索
  const onClickSearchAssigned = async (pagingFlg?: boolean | null): Promise<void> => {
    if (!areaSelectValuesSectionAssigned.value.area_code) return;
    if (pagingFlg !== true) {
      assignedListOptions.page = 1;
      // 新規検索時は選択状態とキャッシュをクリア
      selectedItemsMap.clear();
      selectListCheckboxSectionAssigned.value = [];
    }

    // 配達区画登録済一覧 input [limit 取得件数 offset:取得開始位置]
    const listInput: DeliveryAreaSectionListInput = {
      area_code: areaSelectValuesSectionAssigned.value.area_code,
      chain_code: deliveryAreaInput.chain_code,
      customer_code: localStorageGetCustomerCode(),
      shop_code: shopSelectValues.value.shop_code,
      zip_code: searchConditionZipCodeSectionAssigned.value || undefined,
      limit: assignedListOptions.itemsPerPage,
      offset: (assignedListOptions.page - 1) * assignedListOptions.itemsPerPage,
    };

    // 配達区画未登録件数
    const countInput: DeliveryAreaSectionCountInput = {
      area_code: listInput.area_code,
      chain_code: listInput.chain_code,
      customer_code: listInput.customer_code,
      shop_code: listInput.shop_code,
      zip_code: listInput.zip_code,
    };

    try {
      const apolloClient = await generateApolloClient();
      const result = await apolloClient.query<ListDeliveryAreaSectionAssignedQuery>({
        query: ListDeliveryAreaSectionAssignedDocument,
        variables: { input: listInput, countInput },
        fetchPolicy: "no-cache",
      });
      if (!result.data.listDeliveryAreaSectionAssigned || !result.data.getDeliveryAreaSectionAssignedCount) {
        console.error("登録済区画取得エラー");
        return;
      }
      listAreaSectionAssigned.value = result.data.listDeliveryAreaSectionAssigned;
      listDataTotalCountSectionAssigned.value = result.data.getDeliveryAreaSectionAssignedCount.count;
      const maxPage = pageCountSectionAssigned.value;
      if (assignedListOptions.page > maxPage) {
        assignedListOptions.page = maxPage;
      }
      displayPagingRangeAssigned.value = calcDisplayPagingRange(
        assignedListOptions.page - 1,
        assignedListOptions.itemsPerPage,
        listDataTotalCountSectionAssigned.value
      );
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "不明なエラーが発生しました。";
    }
  };

  // 一括削除確認
  const onClickCheckAssignedSection = async (): Promise<void> => {
    if (selectListCheckboxSectionAssigned.value.length === 0) return;
    displayConfirmDialogSectionAssigned.value = true;
  };

  // 一括削除実行
  const onClickSectionAssignedComplete = async (): Promise<void> => {
    overlay.value = true;
    try {
      // selectListCheckboxSectionAssignedは既にlot_codeの配列なので、そのまま使用
      const lot_list = selectListCheckboxSectionAssigned.value;
      const success = await bulkDeleteExecute(
        localStorageGetCustomerCode(),
        chainSelectValues.value.chain_code,
        shopSelectValues.value.shop_code,
        areaSelectValuesSectionAssigned.value.area_code,
        lot_list,
        localStorageGetUserId()
      );
      displayConfirmDialogSectionAssigned.value = false;
      overlay.value = false;
      if (success) {
        selectListCheckboxSectionAssigned.value = [];
        selectedItemsMap.clear();
        await onClickSearchAssigned();
      } else {
        errMessage.value = `ロットコード ${lot_list.join(",")} の削除に失敗しました。`;
        errdialog.value = true;
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "不明なエラーが発生しました。";
      overlay.value = false;
    }
  };

  return {
    selectedAssignedItems,
    pageCountSectionAssigned,
    assignedListOptions,
    onUpdateAssignedOptions,
    onClickSearchAssigned,
    onClickCheckAssignedSection,
    onClickSectionAssignedComplete,
    onSelectionChangeAssigned,
  };
};
